
import React from 'react'
import { DropdownMenuWrapper, type MenuItem } from '@/components/ui/dropdown-menu/wrapper'
import { MenuIcon } from './MenuIcon'
import type { Canvas } from '@/hooks/useCanvas'
import {
  contextMenuConfigs,
  actionLabels,
  actionIcons,
  actionShortcuts,
  destructiveActions,
  type ContextMenuType,
  type CanvasAction,
} from './context-menu-configs'

interface CanvasContextMenuProps {
  canvas: Canvas
  contextMenuType: ContextMenuType
  onAction: (action: CanvasAction, canvas: Canvas) => void
  children: React.ReactNode
}

export const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
  canvas,
  contextMenuType,
  onAction,
  children,
}) => {
  const actions = contextMenuConfigs[contextMenuType]
  const [open, setOpen] = React.useState(false)

  // Memoize menu items to prevent re-renders
  const menuItems = React.useMemo(() => {
    const items: MenuItem[] = [
      { type: 'label', label: 'Session Actions' },
    ]

    actions.forEach((action, index) => {
      // Add separator before destructive actions or 'move'
      if ((destructiveActions.includes(action) || action === 'move') && index > 0) {
        items.push({ type: 'separator' })
      }

      items.push({
        type: 'item',
        label: actionLabels[action],
        icon: <MenuIcon name={actionIcons[action]} className="h-4 w-4" />, // Use static MenuIcon
        shortcut: actionShortcuts[action],
        variant: destructiveActions.includes(action) ? 'danger' : 'default',
        onClick: () => {
          // Immediately trigger the action, modal will handle focus properly
          onAction(action, canvas)
          // Close dropdown after action to prevent focus conflicts
          setOpen(false)
        },
      })
    })

    return items
  }, [actions, canvas, onAction])

  return (
    <DropdownMenuWrapper
      items={menuItems}
      align="end"
      sideOffset={4}
      modal={true}
      open={open}
      onOpenChange={setOpen}
    >
      {children}
    </DropdownMenuWrapper>
  )
}